import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiEllipsisVertical } from "react-icons/hi2";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "../../../../../../components/button/Button";
import { Card } from "../../../../../../components/card/Card";
import { Dropdown } from "../../../../../../components/dropdown/new-dropdown/Dropdown";
import { WorkDetailsModel } from "../../../../../../factories/work/model/work-details.model";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import { useGetUsersQuery } from "../../../../../../slices/UserSlice";
import { useUpdateStatusWorkMutation } from "../../../../../../slices/WorkSlice";
import { useGetWorkTasksQuery } from "../../../../../../slices/WorkTasksSlice";
import { useGetWorkTypeQuery } from "../../../../../../slices/WorkTypeSlice";
import { WorkTaskModal } from "../../../../modals/WorkTaskModal";
import { useToggleAllSubtasks } from "./useToggleAllSubtasks";
import { WorkEditTemplate } from "./WorkEditTemplate";
import { WorkTasksList } from "./WorkTasksList";

type Props = {
  work: WorkDetailsModel;
};

export const WorkTasks: FC<Props> = ({ work }) => {
  const { workId } = useParams();
  const navigate = useNavigate();
  const checkboxRef = useRef<HTMLInputElement>(null);
  const completingRef = useRef<Promise<void> | null>(null);

  const [allChecked, setAllChecked] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [isOpenAdd, setIsOpenAdd] = useState(false);

  const { data: workType } = useGetWorkTypeQuery({ id: work.serviceId });
  const { data: users } = useGetUsersQuery(getLinkedFirm()?.orgId);

  const { data: workTasksData, refetch: refetchWorkTasks } =
    useGetWorkTasksQuery({
      workId: workId || "",
    });

  const [updateStatusWork, { isLoading: isUpdatingWorkStatus }] =
    useUpdateStatusWorkMutation();

  const workTasks = useMemo(() => workTasksData || [], [workTasksData]);

  const completedStatus = useMemo(
    () =>
      workType?.serviceStatuses?.find((status) => status.name === "Completed"),
    [workType?.serviceStatuses],
  );
  const completedStatusId = completedStatus?.id || null;

  const hasTasks = workTasks.length > 0;

  const { toggleAll, isBusy: isBulkUpdating } = useToggleAllSubtasks({
    workId,
    workTypeId: work.serviceId,
    workTasks,
    onDone: refetchWorkTasks,
  });

  const isDisabled =
    !workId || !hasTasks || isBulkUpdating || isUpdatingWorkStatus;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleTasksStateChange = useCallback(
    ({
      allChecked: nextAllChecked,
      isIndeterminate: nextIsIndeterminate,
    }: {
      allChecked: boolean;
      isIndeterminate: boolean;
    }) => {
      setAllChecked(nextAllChecked);
      setIsIndeterminate(nextIsIndeterminate);
    },
    [],
  );

  const updateWorkStatusToCompleted = useCallback(async () => {
    if (!workId) {
      return;
    }

    if (!completedStatus || !completedStatusId) {
      toast("Completed status was not found", {
        type: "error",
        pauseOnHover: false,
      });

      return;
    }

    if (work.jobStatusId === completedStatusId) {
      return;
    }

    // SUB-014 (QA batch 17): two subtasks ticked quickly can both find "all
    // done"; send the work's Completed change once, not twice at the same time.
    if (completingRef.current) {
      return completingRef.current;
    }

    completingRef.current = updateStatusWork({
      workId,
      statusId: completedStatusId,
      subStatusId: "",
    })
      .unwrap()
      .then(() => undefined)
      .finally(() => {
        completingRef.current = null;
      });

    await completingRef.current;
  }, [
    completedStatus,
    completedStatusId,
    updateStatusWork,
    work.jobStatusId,
    workId,
  ]);

  const handleToggleAllTasks = async () => {
    if (isDisabled) {
      return;
    }

    await toggleAll();
  };

  return (
    <Card extraClasses={"shadow-box max-w-full overflow-visible"}>
      <div className={"pt-2"}>
        <div className={"flex justify-between gap-3 px-6 pb-2"}>
          <div className={"flex h-12 min-w-0 items-center gap-2 pl-2"}>
            <input
              ref={checkboxRef}
              type={"checkbox"}
              checked={allChecked}
              disabled={isDisabled}
              onChange={handleToggleAllTasks}
              className={
                "checkbox mr-3 scale-75 rounded-[5px] border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100 disabled:cursor-not-allowed disabled:opacity-40"
              }
            />

            <div className={"flex min-w-0 items-center gap-3"}>
              <div
                className={
                  "shrink-0 text-[17px] font-extrabold text-[#1F2433]"
                }>
                Subtasks
              </div>

              <WorkEditTemplate work={work} isSubtask={true} />
            </div>
          </div>

          <div className={"flex shrink-0 items-center gap-4"}>
            <div
              className={
                "flex cursor-pointer items-center whitespace-nowrap text-[14px] font-bold text-[#6B4EFF]"
              }
              onClick={() => setIsOpenAdd(true)}>
              Add Subtask
            </div>

            <div>
              <Dropdown
                triggerElement={
                  <Button
                    label={""}
                    colorType={"ghost"}
                    size={"custom"}
                    icon={<HiEllipsisVertical size={20} />}
                    extraClasses={"btn-xs p-0.5"}
                  />
                }>
                <div
                  className={
                    "rounded-[5px] border border-gray-300 bg-white text-sm shadow-box"
                  }>
                  <div
                    className={
                      "cursor-pointer rounded-[5px] px-[15px] py-[5px] text-center hover:bg-gray-300"
                    }
                    onClick={() =>
                      navigate(
                        `/settings/templates/task-templates/view/${work.template.id}`,
                      )
                    }>
                    Edit existing template
                  </div>

                  <div
                    className={
                      "cursor-pointer rounded-[5px] px-[15px] py-[5px] text-center hover:bg-gray-300"
                    }
                    onClick={() =>
                      navigate("/settings/templates/task-templates/view/add")
                    }>
                    Create new template
                  </div>
                </div>
              </Dropdown>
            </div>
          </div>
        </div>

        <WorkTasksList
          work={work}
          users={users}
          onTasksStateChange={handleTasksStateChange}
          onAllTasksCompleted={updateWorkStatusToCompleted}
        />

        {isOpenAdd && (
          <WorkTaskModal
            isActive={isOpenAdd}
            onClose={() => setIsOpenAdd(false)}
            work={work}
            users={users}
          />
        )}
      </div>
    </Card>
  );
};
